import React, { useState, useMemo } from 'react'
import { ChevronDown, ChevronUp, Star } from 'lucide-react'
import { Card, CardContent } from '../ui/Card'
import { Button } from '../ui/Button'
import { FAQItem, FAQCategory } from '../../types/support.types'
import { SearchEngine, HighlightUtils } from '../../utils/search.utils'

interface FAQSectionProps {
  faqs: FAQItem[]
  categories: FAQCategory[]
  searchQuery?: string
  className?: string
}

interface FAQItemProps {
  faq: FAQItem
  isExpanded: boolean
  onToggle: () => void
  searchQuery?: string
  searchScore?: number
  matchedFields?: string[]
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ 
  faq, 
  isExpanded, 
  onToggle, 
  searchQuery,
  searchScore,
  matchedFields = []
}) => {

  return (
    <Card className={`mb-3 ${searchScore && searchScore > 50 ? 'ring-2 ring-blue-200' : ''}`}>
      <CardContent className="p-0">
        <Button
          variant="ghost"
          onClick={onToggle}
          className="w-full p-6 text-left justify-between hover:bg-accent/50 rounded-lg transition-colors"
        >
          <div className="flex-1 pr-4">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-base font-medium text-foreground">
                  {searchQuery ? (
                    <>
                      {HighlightUtils.highlightText(faq.question, searchQuery).map((part, index) => 
                        part.isHighlighted ? (
                          <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
                            {part.text}
                          </mark>
                        ) : (
                          <span key={index}>{part.text}</span>
                        )
                      )}
                    </>
                  ) : faq.question}
                </div>
                
                {/* Search metadata */}
                {searchQuery && (searchScore || matchedFields.length > 0) && (
                  <div className="flex items-center gap-2 mt-1">
                    {searchScore && searchScore > 50 && (
                      <div className="flex items-center gap-1 text-xs text-blue-600">
                        <Star className="h-3 w-3 fill-current" />
                        <span>High relevance</span>
                      </div>
                    )}
                    {matchedFields.length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        Matches: {matchedFields.join(', ')}
                      </div>
                    )}
                    {searchScore && (
                      <div className="text-xs text-muted-foreground">
                        Score: {Math.round(searchScore)}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {faq.priority && faq.priority <= 3 && (
                <div className="flex-shrink-0">
                  <div className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    Popular
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          )}
        </Button>
        
        {isExpanded && (
          <div className="px-6 pb-6 pt-0">
            <div className="text-muted-foreground leading-relaxed border-t pt-4">
              {searchQuery ? (
                <>
                  {HighlightUtils.highlightText(faq.answer, searchQuery).map((part, index) => 
                    part.isHighlighted ? (
                      <mark key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
                        {part.text}
                      </mark>
                    ) : (
                      <span key={index}>{part.text}</span>
                    )
                  )}
                </>
              ) : faq.answer}
            </div>
            
            {/* Keywords display */}
            {faq.keywords && faq.keywords.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                <div className="text-xs text-muted-foreground mb-2">Related topics:</div>
                <div className="flex flex-wrap gap-1">
                  {faq.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-block text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded-md"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export const FAQSection: React.FC<FAQSectionProps> = ({ 
  faqs, 
  categories, 
  searchQuery = '', 
  className = '' 
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const filteredData = useMemo(() => {
    if (!searchQuery) {
      // Group FAQs by category when no search
      const grouped = categories.map(category => ({
        category,
        faqs: faqs
          .filter(faq => faq.category === category.id)
          .sort((a, b) => (a.priority || 999) - (b.priority || 999))
      })).filter(group => group.faqs.length > 0)
      
      return { grouped, total: faqs.length }
    } else {
      // Use advanced search engine
      const searchResults = SearchEngine.searchFAQs(faqs, searchQuery, {
        maxResults: 50,
        minScore: 5,
        includeKeywords: true,
        fuzzyMatch: true
      })

      return { searchResults, total: searchResults.length }
    }
  }, [faqs, categories, searchQuery])

  const toggleExpanded = (faqId: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(faqId)) {
        newSet.delete(faqId)
      } else {
        newSet.add(faqId)
      }
      return newSet
    })
  }

  const expandAll = () => {
    const allIds = searchQuery 
      ? (filteredData as any).searchResults?.map((result: any) => result.item.id) || []
      : faqs.map(faq => faq.id)
    setExpandedItems(new Set(allIds))
  }

  const collapseAll = () => {
    setExpandedItems(new Set())
  }

  if (searchQuery && (filteredData as any).searchResults?.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="bg-card border rounded-lg p-8">
          <h3 className="text-xl font-semibold text-foreground mb-2">
            No results found
          </h3>
          <p className="text-muted-foreground mb-4">
            Couldn't find what you're looking for? Try different keywords or contact our support team.
          </p>
          <Button 
            onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            Submit a Request
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Results Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">
            {searchQuery ? 'Search Results' : 'Frequently Asked Questions'}
          </h2>
          <p className="text-muted-foreground mt-1">
            {searchQuery 
              ? `${filteredData.total} result${filteredData.total !== 1 ? 's' : ''} found`
              : `${filteredData.total} questions organized by category`
            }
          </p>
        </div>
        
        {filteredData.total > 0 && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={expandAll}
              className="text-xs"
            >
              Expand All
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={collapseAll}
              className="text-xs"
            >
              Collapse All
            </Button>
          </div>
        )}
      </div>

      {/* FAQ Content */}
      {searchQuery ? (
        // Search Results View
        <div className="space-y-2">
          {(filteredData as any).searchResults?.map((result: any) => (
            <FAQItemComponent
              key={result.item.id}
              faq={result.item}
              isExpanded={expandedItems.has(result.item.id)}
              onToggle={() => toggleExpanded(result.item.id)}
              searchQuery={searchQuery}
              searchScore={result.matchScore}
              matchedFields={result.matchedFields}
            />
          ))}
        </div>
      ) : (
        // Categorized View
        <div className="space-y-8">
          {(filteredData as any).grouped?.map(({ category, faqs: categoryFaqs }: any) => (
            <div key={category.id}>
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-foreground mb-1">
                  {category.name}
                </h3>
                {category.description && (
                  <p className="text-muted-foreground text-sm">
                    {category.description}
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                {categoryFaqs.map((faq: FAQItem) => (
                  <FAQItemComponent
                    key={faq.id}
                    faq={faq}
                    isExpanded={expandedItems.has(faq.id)}
                    onToggle={() => toggleExpanded(faq.id)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}